import { ChangeDetectorRef, Component, NgZone, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { AuthorizationService } from 'services/authorization.service';
import { BaseModalComponent } from '../base-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';

export const MS_IN_SECOND = 1000;
export const SECONDS_IN_MINUTE = 60;
export const KEY_LAST_ACTION = 'lastAction';
export const SHOW_TIME = 240;
export const LOGOUT_TIME = 245;
export const SHOW_LOGOUT_MODAL_TIME = SHOW_TIME * SECONDS_IN_MINUTE * MS_IN_SECOND;
export const AUTO_LOGOUT_TIME = LOGOUT_TIME * SECONDS_IN_MINUTE * MS_IN_SECOND;

@Component({
  selector: 'app-auto-logout-modal',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './auto-logout-modal.component.html',
  styleUrl: './auto-logout-modal.component.scss'
})
export class AutoLogoutModalComponent extends BaseModalComponent implements OnDestroy {
  public isLogoutModalVisible: boolean = false;
  public timePassed: number = 0;
  public secondsRemaining = 0;
  private intervalId: any = null;
  @ViewChild('content') content!: TemplateRef<any>;

  constructor(
    config: NgbModalConfig,
    modalService: NgbModal,
    private authService: AuthorizationService,
    private changeDetector: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    super(config, modalService);
    this.setLastAction(Date.now());
    this.initListener();
    this.initInterval();
  }

  ngOnDestroy(): void {
    document.body.removeEventListener('click', () => this.resetTimer());
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  getLastAction(): string {
    return localStorage.getItem(KEY_LAST_ACTION) || '';
  }

  setLastAction(date: number): void {
    this.refreshAccessTokenIfNeeded();
    localStorage.setItem(KEY_LAST_ACTION, JSON.stringify(date));
  }

  initListener() {
    this.ngZone.runOutsideAngular(() => {
      document.body.addEventListener('click', () => this.resetTimer());
    });
  }

  initInterval() {
    this.ngZone.runOutsideAngular(() => {
      this.intervalId = setInterval(() => {
        this.check();
      }, MS_IN_SECOND);
    });
  }

  resetTimer(): void {
    if (!this.isLogoutModalVisible) {
      this.setLastAction(Date.now());
    }
  }

  check() {
    this.timePassed = Date.now() - parseInt(this.getLastAction());

    if (this.isLogoutModalVisible) {
      const secondsRemaining = Math.round((AUTO_LOGOUT_TIME - this.timePassed) / MS_IN_SECOND);
      this.secondsRemaining = secondsRemaining > 0 ? secondsRemaining : 0;
      this.changeDetector.detectChanges();
    } else {
      if (this.timePassed >= SHOW_LOGOUT_MODAL_TIME && !this.isLogoutModalVisible) {
        const secondsRemaining = Math.round((AUTO_LOGOUT_TIME - this.timePassed) / MS_IN_SECOND);
        this.secondsRemaining = secondsRemaining > 0 ? secondsRemaining : 0;
        this.isLogoutModalVisible = true;
        this.changeDetector.detectChanges();
        this.open(this.content);
      } else {
        this.isLogoutModalVisible = false;
        this.changeDetector.detectChanges();
      }
    }

    if (this.timePassed >= AUTO_LOGOUT_TIME) {
      this.isLogoutModalVisible = false;
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
      this.logOut();
    }
  }

  refreshAccessTokenIfNeeded(): void {
    // TODO: Implement refreshAccessTokenIfNeeded
  }

  getSecondsLeft(): number {
    return Math.round((AUTO_LOGOUT_TIME - parseInt(this.getLastAction())) / MS_IN_SECOND);
  }

  logOut(): void {
    this.close();
    this.modalService.dismissAll();
    this.authService.logout();
  }

  continueSession(): void {
    this.setLastAction(Date.now());
    this.isLogoutModalVisible = false;
    this.close();
    this.changeDetector.detectChanges();
  }

  formatRemainingTime(seconds: number): string {
    const minutes = Math.floor(seconds / SECONDS_IN_MINUTE);
    const remainingSeconds = seconds % SECONDS_IN_MINUTE;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
}
